import { useCallback, useEffect, useState } from 'react';

const CHAVE = 'fiscoai:menu-recolhido';
/** Sem preferência gravada, telas abaixo de 1280 px começam com o menu recolhido. */
const TELA_ESTREITA = '(max-width: 1279.98px)';
/** O menu lateral fixo só existe a partir do `lg` (64rem); abaixo dele é a gaveta. */
const TELA_DESKTOP = '(min-width: 64rem)';

function corresponde(consulta: string): boolean {
  try {
    return globalThis.matchMedia(consulta).matches;
  } catch {
    return false;
  }
}

/** Verdadeiro quando o menu lateral fixo está em cena (lg+). */
export function ehDesktop(): boolean {
  return corresponde(TELA_DESKTOP);
}

function lerRecolhida(): boolean {
  try {
    const gravado = globalThis.localStorage.getItem(CHAVE);
    if (gravado !== null) return gravado === '1';
  } catch {
    // sem storage: cai no padrão pela largura
  }
  return corresponde(TELA_ESTREITA);
}

function gravar(recolhida: boolean): void {
  try {
    globalThis.localStorage.setItem(CHAVE, recolhida ? '1' : '0');
  } catch {
    // sem storage: vale só nesta sessão
  }
}

/** Quem está digitando não pode ter o atalho roubado (Ctrl+B é negrito em muitos editores). */
function focoEmCampo(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) return false;
  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(alvo.tagName)) return true;
  return alvo.closest('[contenteditable]:not([contenteditable="false"])') !== null;
}

function ehAtalho(evento: KeyboardEvent): boolean {
  return (
    evento.key.toLowerCase() === 'b' &&
    (evento.metaKey || evento.ctrlKey) &&
    !evento.altKey &&
    !evento.shiftKey
  );
}

function ehMac(): boolean {
  const nav: { userAgentData?: { platform?: string }; userAgent: string } = globalThis.navigator;
  return /mac|iphone|ipad/i.test(nav.userAgentData?.platform ?? nav.userAgent);
}

/** Rótulo do atalho para a plataforma: "⌘B" no Mac, "Ctrl+B" nas demais. */
export function atalhoMenu(): string {
  return ehMac() ? '⌘B' : 'Ctrl+B';
}

/**
 * Estado do menu lateral recolhido, lembrado no navegador, com o atalho Ctrl+B / ⌘B (só no
 * desktop e nunca com o foco num campo de texto).
 */
export function useMenuRecolhido(): [boolean, () => void] {
  const [recolhida, setRecolhida] = useState(lerRecolhida);

  const alternar = useCallback(() => {
    setRecolhida((atual) => {
      gravar(!atual);
      return !atual;
    });
  }, []);

  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      if (!ehAtalho(evento) || focoEmCampo(evento.target) || !ehDesktop()) return;
      evento.preventDefault();
      alternar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => {
      window.removeEventListener('keydown', aoTeclar);
    };
  }, [alternar]);

  return [recolhida, alternar];
}
